import clsx from "clsx";
import {
  BookOpen,
  Check,
  ChevronDown,
  ChevronRight,
  Circle,
  CircleAlert,
  LoaderCircle,
  Pause,
  Square,
  Wrench,
} from "lucide-react";
import { useId, useState } from "react";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";

import {
  getActivityPreview,
  getToolDisplayStatus,
  groupActivitySteps,
  type ActivityState,
  type ActivityStep,
  type ActivityTool,
} from "@/lib/activity";
import { useStore } from "@/lib/store";

function formatDuration(ms: number): string {
  return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function ToolRow({ tool, state }: { tool: ActivityTool; state: ActivityState }) {
  const status = getToolDisplayStatus(tool, state);
  const Icon = {
    pending: Circle,
    running: LoaderCircle,
    completed: Check,
    error: CircleAlert,
    paused: Pause,
    interrupted: Square,
  }[status];
  const label = {
    pending: "Queued",
    running: "Running",
    completed: "Completed",
    error: "Failed",
    paused: "Paused",
    interrupted: "Interrupted",
  }[status];

  return (
    <li className="text-cyber-muted flex min-w-0 items-center gap-2 py-0.5 text-xs">
      <Icon
        size={13}
        aria-hidden="true"
        className={
          status === "running"
            ? "text-cyber-magenta shrink-0 motion-safe:animate-spin"
            : status === "error"
              ? "text-cyber-red shrink-0"
              : "shrink-0"
        }
      />
      <span className="sr-only">{label}: </span>
      {tool.name === "skill" && <BookOpen size={13} className="shrink-0" aria-hidden="true" />}
      <span className="min-w-0 flex-1 truncate" title={tool.title ?? tool.name}>
        <span className="text-cyber-text/80">{tool.name}</span>
        {tool.title && <span className="text-cyber-muted/80"> — {tool.title}</span>}
      </span>
      {status !== "completed" && <span className="shrink-0 text-[10px]">{label}</span>}
      {tool.durationMs != null && (
        <span className="text-cyber-muted/60 shrink-0 text-[10px]">
          {formatDuration(tool.durationMs)}
        </span>
      )}
    </li>
  );
}

function ToolGroup({
  tools,
  state,
  showRecent,
}: {
  tools: ActivityTool[];
  state: ActivityState;
  showRecent: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const id = useId();
  const preview = getActivityPreview(tools, showRecent);
  const hiddenCount = tools.length - preview.length;
  const visible = expanded ? tools : preview;
  const skills = tools.filter((tool) => tool.name === "skill").length;
  const failed = tools.filter((tool) => tool.status === "error").length;
  const summary = `${tools.length} tool call${tools.length === 1 ? "" : "s"}${skills ? ` · ${skills} skill${skills === 1 ? "" : "s"}` : ""}${failed ? ` · ${failed} failed` : ""}`;

  return (
    <div className="border-cyber-border/40 bg-cyber-bg/30 min-w-0 rounded border px-2.5 py-1.5">
      <div className="text-cyber-muted flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-[11px]">
        <span className="flex items-center gap-1.5">
          <Wrench size={12} aria-hidden="true" />
          {summary}
        </span>
        {hiddenCount > 0 && (
          <button
            type="button"
            aria-expanded={expanded}
            aria-controls={id}
            onClick={() => setExpanded(!expanded)}
            className="hover:text-cyber-text inline-flex items-center gap-1 transition-colors"
          >
            {expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
            {expanded ? "Hide details" : "Show details"}
          </button>
        )}
      </div>
      <ul
        id={id}
        aria-label="Tool activity"
        className={visible.length ? "mt-1 max-h-60 space-y-0.5 overflow-y-auto" : ""}
      >
        {visible.map((tool, index) => (
          <ToolRow key={tool.id ?? index} tool={tool} state={state} />
        ))}
      </ul>
    </div>
  );
}

export function ActivityTimeline({
  steps,
  state = "finished",
}: {
  steps: ActivityStep[];
  state?: ActivityState;
}) {
  const theme = useStore((state) => state.theme);
  const groups = groupActivitySteps(steps);
  return (
    <div className="min-w-0 space-y-3">
      {groups.map((group, index) => (
        <div key={group.id} className="min-w-0 space-y-2">
          {group.text && (
            <div
              className={clsx(
                "prose prose-sm text-cyber-text/80 prose-p:my-1 prose-a:text-cyber-cyan prose-pre:overflow-x-auto max-w-none text-sm break-words",
                theme === "cyber" && "prose-invert",
              )}
            >
              <Markdown remarkPlugins={[remarkGfm]}>{group.text}</Markdown>
            </div>
          )}
          {group.tools.length > 0 && (
            <ToolGroup
              tools={group.tools}
              state={state}
              showRecent={
                state !== "finished" &&
                (index === groups.length - 1 ||
                  group.tools.some(
                    (tool) => tool.status === "running" || tool.status === "pending",
                  ))
              }
            />
          )}
        </div>
      ))}
    </div>
  );
}
